import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useFormatPrice from "@/hooks/use-format-price";
import {
  ReturnRequestLineItemNewVariantDto,
  ReturnRequestLineItemVariantDto,
} from "@babackapp/private-api-client/types";
import {
  getVariantOptionsLabel,
  shopifyThumbnail,
} from "@babackapp/return-variables-registry";
import useTranslation from "@babackapp/translations/use-translation";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@babackapp/ui/components/hover-card";
import type { ExchangedArticle } from "./exchanged-article";

type SimpleExchangeHoverCardProps = {
  initialVariant: ReturnRequestLineItemVariantDto;
  newVariant?: ReturnRequestLineItemNewVariantDto;
  originalPrice?: string;
  newVariantPrice?: number;
  /** Set when the customer gets another article: shown with its photo. */
  exchangedArticle?: ExchangedArticle | null;
};

export function SimpleExchangeHoverCard({
  children,
  initialVariant,
  newVariant,
  originalPrice,
  newVariantPrice,
  exchangedArticle,
}: React.PropsWithChildren<SimpleExchangeHoverCardProps>) {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const formatPrice = useFormatPrice();
  const currency = shop.preferences.currency;

  const priceComparison = (() => {
    if (originalPrice === undefined || newVariantPrice === undefined)
      return null;
    const oldAmount = parseFloat(originalPrice);
    if (isNaN(oldAmount)) return null;
    const delta = newVariantPrice - oldAmount;
    return {
      old: formatPrice({ amount: oldAmount, currency }),
      new: formatPrice({ amount: newVariantPrice, currency }),
      delta: `${delta > 0 ? "+" : ""}${formatPrice({ amount: delta, currency })}`,
      hasDiff: delta !== 0,
    };
  })();

  const newVariantLabel =
    getVariantOptionsLabel(
      [newVariant?.option1, newVariant?.option2, newVariant?.option3],
      " / ",
    ) || newVariant?.productTitle;

  // The line already shows the new variant next to the struck initial one:
  // only the card repeats it.
  return (
    <>
      <HoverCard>
        <HoverCardTrigger>{children}</HoverCardTrigger>
        <HoverCardContent>
          <div className="flex flex-col gap-2">
            <div className="flex flex-col">
              <span className="text-muted-foreground font-medium text-xs">
                {t("returnDetail.update.initialVariant")}
              </span>
              <p className="text-sm text-foreground font-medium">
                {initialVariant.title}
              </p>
            </div>
            <div className="flex flex-col">
              <span className="text-muted-foreground font-medium text-xs">
                {t("returnDetail.update.newVariant")}
              </span>
              {exchangedArticle ? (
                <div className="flex items-center gap-2 pt-1">
                  <img
                    src={shopifyThumbnail(exchangedArticle.image, 96)}
                    alt={exchangedArticle.title ?? exchangedArticle.label}
                    className="size-12 shrink-0 rounded-md object-cover"
                  />
                  <div className="flex flex-col">
                    {exchangedArticle.title && (
                      <p className="text-sm text-foreground font-medium">
                        {exchangedArticle.title}
                      </p>
                    )}
                    {exchangedArticle.label && (
                      <p className="text-xs text-muted-foreground">
                        {exchangedArticle.label}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-foreground font-medium">
                  {newVariantLabel}
                </p>
              )}
            </div>
            {priceComparison && (
              <div className="flex flex-col gap-1 border-t pt-2">
                <span className="text-muted-foreground font-medium text-xs">
                  {t("returnDetail.update.originalPrice")}
                </span>
                <p className="text-sm text-foreground font-medium">
                  {priceComparison.old} → {priceComparison.new}
                </p>
                {priceComparison.hasDiff && (
                  <p className="text-xs font-medium text-amber-600">
                    {priceComparison.delta}
                  </p>
                )}
              </div>
            )}
          </div>
        </HoverCardContent>
      </HoverCard>
    </>
  );
}
